/**
 * ProjectSettingsModal - modal for editing project settings
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { clsx } from 'clsx';
import { X, Trash2, AlertTriangle, Archive, Tag } from 'lucide-react';
import { useProjectStore } from '@stores/projectStore';
import { useTaskStore } from '@stores/taskStore';
import LabelManager from '@components/board/LabelManager';
import { toast } from '@components/ui/Toast';
import type { Project, ProjectSettings } from '@services/api';

interface ProjectSettingsModalProps {
  project: Project | null;
  open: boolean;
  onClose: () => void;
  onDelete?: () => void;
}

const colorOptions = [
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#ec4899', // Pink
  '#ef4444', // Red
  '#f97316', // Orange
  '#eab308', // Yellow
  '#22c55e', // Green
  '#14b8a6', // Teal
  '#06b6d4', // Cyan
  '#3b82f6', // Blue
];

const viewOptions = [
  { value: 'kanban', labelKey: 'project.viewKanban' },
  { value: 'list', labelKey: 'project.viewList' },
  { value: 'timeline', labelKey: 'settings.viewTimeline' },
  { value: 'calendar', labelKey: 'settings.viewCalendar' },
  { value: 'table', labelKey: 'project.viewTable' },
] as const;

export default function ProjectSettingsModal({
  project,
  open,
  onClose,
  onDelete,
}: ProjectSettingsModalProps) {
  const { t } = useTranslation();
  const { updateProject, archiveProject, deleteProject } = useProjectStore();
  const { labels, fetchLabels } = useTaskStore();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<string | undefined>(undefined);
  const [isPublic, setIsPublic] = useState(false);

  // Settings
  const [defaultBoardView, setDefaultBoardView] = useState<ProjectSettings['defaultBoardView']>('kanban');
  const [enableTimeTracking, setEnableTimeTracking] = useState(true);
  const [enableSubtasks, setEnableSubtasks] = useState(true);
  const [enableDependencies, setEnableDependencies] = useState(true);
  const [enableCustomFields, setEnableCustomFields] = useState(true);

  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Reset form when project changes
  useEffect(() => {
    if (project) {
      setName(project.name);
      setDescription(project.description || '');
      setColor(project.color);
      setIsPublic(project.isPublic);
      setDefaultBoardView(project.settings?.defaultBoardView || 'kanban');
      setEnableTimeTracking(project.settings?.enableTimeTracking ?? true);
      setEnableSubtasks(project.settings?.enableSubtasks ?? true);
      setEnableDependencies(project.settings?.enableDependencies ?? true);
      setEnableCustomFields(project.settings?.enableCustomFields ?? true);
      setShowDeleteConfirm(false);
    }
  }, [project]);

  // Fetch labels when project changes
  useEffect(() => {
    if (project?.id && open) {
      fetchLabels(project.id).catch((err) =>
        console.error('Failed to fetch labels:', err)
      );
    }
  }, [project?.id, open, fetchLabels]);

  // Local labels state for optimistic updates in the manager
  const [localLabels, setLocalLabels] = useState(labels);
  useEffect(() => {
    setLocalLabels(labels);
  }, [labels]);

  const handleSave = async () => {
    if (!project || !name.trim()) return;

    setIsSaving(true);
    try {
      const updateData: Partial<Project> = {
        name: name.trim(),
        isPublic,
      };

      if (description.trim()) {
        updateData.description = description.trim();
      }

      if (color) {
        updateData.color = color;
      }

      await updateProject(project.id, updateData);

      // Note: settings update would need a separate API call via projectsApi.updateSettings
      // For now we only update the basic project info

      toast.success(t('project.settingsUpdated'));
      onClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('project.settingsUpdateError');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!project) return;

    setIsSaving(true);
    try {
      await archiveProject(project.id);
      toast.success(t('project.archiveSuccess'));
      onClose();
      onDelete?.();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('project.archiveError');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!project) return;

    setIsSaving(true);
    try {
      await deleteProject(project.id);
      toast.success(t('project.deleteSuccess'));
      onClose();
      onDelete?.();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('project.deleteError');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!project) return null;

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={clsx(
            'fixed inset-0 z-50 bg-black/50',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          )}
        />
        <Dialog.Content
          className={clsx(
            'fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2',
            'max-h-[90vh] overflow-y-auto',
            'rounded-lg bg-white p-6 shadow-xl dark:bg-neutral-800',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95'
          )}
        >
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-white">
              {t('project.settings')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className={clsx(
                  'rounded p-1 transition-colors',
                  'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700',
                  'dark:hover:bg-neutral-700 dark:hover:text-neutral-300'
                )}
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Form */}
          <div className="space-y-6">
            {/* Basic Info Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('settings.basicInfo')}
              </h3>

              {/* Name */}
              <div>
                <label
                  htmlFor="project-name"
                  className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300"
                >
                  {t('common.name')}
                </label>
                <input
                  id="project-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={clsx(
                    'w-full rounded-md border px-3 py-2',
                    'border-neutral-300 bg-white text-neutral-900',
                    'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                    'dark:border-neutral-600 dark:bg-neutral-700 dark:text-white'
                  )}
                  placeholder={t('project.namePlaceholder')}
                />
              </div>

              {/* Description */}
              <div>
                <label
                  htmlFor="project-description"
                  className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300"
                >
                  {t('project.description')}
                </label>
                <textarea
                  id="project-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className={clsx(
                    'w-full rounded-md border px-3 py-2',
                    'border-neutral-300 bg-white text-neutral-900',
                    'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                    'dark:border-neutral-600 dark:bg-neutral-700 dark:text-white'
                  )}
                  placeholder={t('project.descriptionPlaceholder')}
                />
              </div>

              {/* Color */}
              <div>
                <label className="mb-2 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  {t('project.projectColor')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      className={clsx(
                        'h-8 w-8 rounded-full transition-transform',
                        color === c && 'ring-2 ring-offset-2 ring-primary-500 scale-110',
                        'hover:scale-105'
                      )}
                      style={{ backgroundColor: c }}
                      title={c}
                    />
                  ))}
                </div>
              </div>

              {/* Visibility */}
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-neutral-900 dark:text-white">
                    {t('project.publicProject')}
                  </span>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    {t('project.publicProjectDescription')}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isPublic}
                  onClick={() => setIsPublic(!isPublic)}
                  className={clsx(
                    'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full',
                    'border-2 border-transparent transition-colors duration-200 ease-in-out',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                    isPublic ? 'bg-primary-600' : 'bg-neutral-200 dark:bg-neutral-600'
                  )}
                >
                  <span
                    className={clsx(
                      'pointer-events-none inline-block h-5 w-5 transform rounded-full',
                      'bg-white shadow ring-0 transition duration-200 ease-in-out',
                      isPublic ? 'translate-x-5' : 'translate-x-0'
                    )}
                  />
                </button>
              </div>
            </div>

            {/* Features Section */}
            <div className="space-y-4 border-t border-neutral-200 pt-4 dark:border-neutral-700">
              <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.features')}
              </h3>

              {/* Default View */}
              <div>
                <label
                  htmlFor="default-view"
                  className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300"
                >
                  {t('settings.defaultBoardView')}
                </label>
                <select
                  id="default-view"
                  value={defaultBoardView}
                  onChange={(e) => setDefaultBoardView(e.target.value as ProjectSettings['defaultBoardView'])}
                  className={clsx(
                    'w-full rounded-md border px-3 py-2',
                    'border-neutral-300 bg-white text-neutral-900',
                    'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                    'dark:border-neutral-600 dark:bg-neutral-700 dark:text-white'
                  )}
                >
                  {viewOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </option>
                  ))}
                </select>
              </div>

              <ToggleOption
                label={t('project.enableTimeTracking')}
                description={t('project.enableTimeTrackingDescription')}
                checked={enableTimeTracking}
                onChange={setEnableTimeTracking}
              />
              <ToggleOption
                label={t('project.enableSubtasks')}
                description={t('project.enableSubtasksDescription')}
                checked={enableSubtasks}
                onChange={setEnableSubtasks}
              />
              <ToggleOption
                label={t('project.enableDependencies')}
                description={t('project.enableDependenciesDescription')}
                checked={enableDependencies}
                onChange={setEnableDependencies}
              />
              <ToggleOption
                label={t('project.enableCustomFields')}
                description={t('project.enableCustomFieldsDescription')}
                checked={enableCustomFields}
                onChange={setEnableCustomFields}
              />
            </div>
          </div>

          {/* Labels Section */}
          <div className="space-y-4 border-t border-neutral-200 pt-4 dark:border-neutral-700">
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-neutral-500" />
              <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('task.labels')}
              </h3>
            </div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {t('project.manageLabelsHint')}
            </p>
            {project && (
              <LabelManager
                projectId={project.id}
                labels={localLabels}
                onLabelsChange={setLocalLabels}
              />
            )}
          </div>

          {/* Danger Zone */}
          <div className="mt-6 border-t border-neutral-200 pt-4 dark:border-neutral-700">
            {!showDeleteConfirm ? (
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={handleArchive}
                  disabled={isSaving}
                  className={clsx(
                    'flex items-center gap-2 text-sm',
                    'text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300',
                    'disabled:cursor-not-allowed disabled:opacity-50'
                  )}
                >
                  <Archive className="h-4 w-4" />
                  {t('project.archiveProject')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className={clsx(
                    'flex items-center gap-2 text-sm',
                    'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300'
                  )}
                >
                  <Trash2 className="h-4 w-4" />
                  {t('project.deleteProject')}
                </button>
              </div>
            ) : (
              <div className="rounded-md bg-red-50 p-3 dark:bg-red-900/20">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      {t('project.confirmDeleteTitle')}
                    </p>
                    <p className="mt-1 text-xs text-red-700 dark:text-red-300">
                      {t('project.confirmDeleteDescription')}
                    </p>
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={isSaving}
                        className={clsx(
                          'rounded px-3 py-1 text-sm font-medium',
                          'bg-red-600 text-white hover:bg-red-700',
                          'disabled:cursor-not-allowed disabled:opacity-50'
                        )}
                      >
                        {t('project.confirmDeleteLabel')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(false)}
                        className={clsx(
                          'rounded px-3 py-1 text-sm font-medium',
                          'bg-neutral-200 text-neutral-700 hover:bg-neutral-300',
                          'dark:bg-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-500'
                        )}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className={clsx(
                'rounded-md px-4 py-2 text-sm font-medium',
                'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
                'dark:bg-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-600'
              )}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !name.trim()}
              className={clsx(
                'rounded-md px-4 py-2 text-sm font-medium',
                'bg-primary-600 text-white hover:bg-primary-700',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            >
              {isSaving ? t('common.saving') : t('common.saveChanges')}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface ToggleOptionProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function ToggleOption({ label, description, checked, onChange }: ToggleOptionProps) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <span className="text-sm text-neutral-900 dark:text-white">{label}</span>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={clsx(
          'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full',
          'border-2 border-transparent transition-colors duration-200 ease-in-out',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
          checked ? 'bg-primary-600' : 'bg-neutral-200 dark:bg-neutral-600'
        )}
      >
        <span
          className={clsx(
            'pointer-events-none inline-block h-5 w-5 transform rounded-full',
            'bg-white shadow ring-0 transition duration-200 ease-in-out',
            checked ? 'translate-x-5' : 'translate-x-0'
          )}
        />
      </button>
    </div>
  );
}
